import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, HardDrive, Check, Sparkles, ShieldCheck, Gift, Copy, FileCheck2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [driveSave, setDriveSave] = useState(user?.auto_drive_save ?? true);
  const [driveSaving, setDriveSaving] = useState(false);
  const [refData, setRefData] = useState(null);

  useEffect(() => { api.get("/referral").then((r) => setRefData(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (params.get("drive") === "connected") { toast.success("Google Drive connected"); refreshUser(); setParams({}); }
    if (params.get("drive_error")) { toast.error("Could not connect Google Drive"); setParams({}); }
  }, []);

  useEffect(() => { setDriveSave(user?.auto_drive_save ?? true); }, [user?.auto_drive_save]);

  const connectDrive = async () => {
    try {
      const res = await api.get("/oauth/drive/login");
      window.location.href = res.data.auth_url;
    } catch (e) { toast.error("Could not start Drive connection"); }
  };

  const disconnectDrive = async () => {
    try { await api.post("/oauth/drive/disconnect"); await refreshUser(); toast.success("Drive disconnected"); }
    catch (e) { toast.error("Could not disconnect"); }
  };

  const toggleDriveSave = async (val) => {
    setDriveSave(val);
    setDriveSaving(true);
    try {
      await api.patch("/settings", { auto_drive_save: val });
      await refreshUser();
      toast.success(val ? "Unlocked files will auto-save to Drive" : "Auto-save to Drive turned off");
    } catch (e) {
      toast.error("Could not save");
      setDriveSave(!val);
    } finally { setDriveSaving(false); }
  };

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-display text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-slate-600 mt-1">Manage storage and privacy preferences.</p>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-6" data-testid="purge-policy-section">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            <h3 className="font-display text-lg font-semibold">Automatic purge (24 hours)</h3>
          </div>
          <p className="text-slate-500 text-sm mt-1">
            For your security, every file is <strong>automatically and permanently purged from our servers 24 hours after generation</strong>.
            Save important files to your device, or connect Google Drive below to keep a permanent copy. You can also wipe any file instantly
            from the preview screen or your Documents list.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-4" data-testid="consent-record-section">
          <div className="flex items-center gap-2">
            <FileCheck2 className="h-5 w-5 text-indigo-600" />
            <h3 className="font-display text-lg font-semibold">Terms & Privacy consent</h3>
          </div>
          {user?.terms_accepted_version ? (
            <p className="text-slate-500 text-sm mt-1" data-testid="consent-record-accepted">
              You accepted the <strong>Terms of Service</strong> and <strong>Privacy Policy</strong>
              {" "}(version <span className="font-mono">{user.terms_accepted_version}</span>)
              {user.terms_accepted_at ? ` on ${new Date(user.terms_accepted_at).toLocaleString()}` : ""}.
              {user.policy_version && user.policy_version !== user.terms_accepted_version && (
                <span className="text-amber-700"> A newer version (<span className="font-mono">{user.policy_version}</span>) is awaiting your acceptance.</span>
              )}
            </p>
          ) : (
            <p className="text-slate-500 text-sm mt-1" data-testid="consent-record-pending">
              We don't have a recorded acceptance yet. You'll be asked to review and accept the current policies.
            </p>
          )}
          <div className="mt-3 flex items-center gap-4 text-sm">
            <a href="/terms" target="_blank" rel="noopener noreferrer" data-testid="consent-terms-link" className="text-indigo-600 hover:text-indigo-700 font-medium">Read Terms</a>
            <a href="/privacy" target="_blank" rel="noopener noreferrer" data-testid="consent-privacy-link" className="text-indigo-600 hover:text-indigo-700 font-medium">Read Privacy Policy</a>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-4" data-testid="drive-section">
          <div className="flex items-center gap-2">
            <HardDrive className="h-5 w-5 text-indigo-600" />
            <h3 className="font-display text-lg font-semibold">Google Drive</h3>
            {user?.drive_connected && <span className="text-xs font-mono bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full flex items-center gap-1"><Check className="h-3 w-3" /> Connected</span>}
          </div>
          <p className="text-slate-500 text-sm mt-1">Link your Google Drive to keep a permanent copy of your files — so nothing is ever lost to the 24-hour purge. We request app-only access: we can only see files Facio Doc creates.</p>

          {user?.drive_connected ? (
            <>
              <div data-testid="drive-autosave-row" className="mt-4 flex items-center justify-between gap-4 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                <div>
                  <p className="text-sm font-semibold">Always save unlocked files to my Google Drive</p>
                  <p className="text-xs text-slate-500 mt-0.5">Every file you unlock is copied to your Drive automatically, so it survives the 24-hour purge.</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {driveSaving && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                  <Switch data-testid="drive-autosave-toggle" checked={driveSave} onCheckedChange={toggleDriveSave} disabled={driveSaving} />
                </div>
              </div>
              <Button data-testid="drive-disconnect" onClick={disconnectDrive} variant="outline" className="mt-4 rounded-full text-red-600 border-red-200 hover:bg-red-50">Disconnect</Button>
            </>
          ) : (
            <Button data-testid="drive-connect" onClick={connectDrive} className="mt-4 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white">Connect Google Drive</Button>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-4" data-testid="referral-section">
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-indigo-600" />
            <h3 className="font-display text-lg font-semibold">Refer &amp; earn</h3>
          </div>
          <p className="text-slate-500 text-sm mt-1">
            Share your link. When a friend signs up and makes their <strong>first purchase</strong>, you earn{" "}
            <strong>{refData?.reward ?? 5} credits</strong> — up to {refData?.cap ?? 20} friends.
          </p>
          <div className="mt-4 flex items-center gap-2">
            <input
              data-testid="referral-link"
              readOnly
              value={refData?.link || "Generating your link…"}
              className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono text-slate-700 truncate"
            />
            <Button
              data-testid="referral-copy"
              onClick={() => { if (refData?.link) { navigator.clipboard.writeText(refData.link); toast.success("Invite link copied"); } }}
              disabled={!refData?.link}
              className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
            >
              <Copy className="h-4 w-4 mr-1.5" /> Copy
            </Button>
          </div>
          <p data-testid="referral-earned" className="text-xs text-slate-500 mt-3">
            {refData ? `${refData.invited ?? 0} invited · ${refData.converted ?? 0} converted · ${refData.earned ?? 0} credits earned` : ""}
          </p>
          <Button
            data-testid="referral-dashboard-link"
            variant="outline"
            onClick={() => navigate("/referrals")}
            className="mt-4 rounded-full border-indigo-200 text-indigo-700 hover:bg-indigo-50"
          >
            View referral dashboard
          </Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-4" data-testid="tour-section">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            <h3 className="font-display text-lg font-semibold">Welcome tour</h3>
          </div>
          <p className="text-slate-500 text-sm mt-1">New to Facio Doc? Replay the quick guided tour of the studio tools and pricing.</p>
          <Button
            data-testid="replay-tour"
            onClick={() => { localStorage.removeItem("facio_tour_seen_v1"); toast.success("Tour will show on the Studio page"); navigate("/studio"); }}
            variant="outline"
            className="mt-4 rounded-full border-indigo-200 text-indigo-700 hover:bg-indigo-50"
          >
            Replay welcome tour
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
